import type { ChatSuggestion } from "@repo/ui/chat/chatEmptyState";

// The proposal workflow, sizing, and delivery rules live in
// getAutomationConfigurationInstructionsTool; starters supply only the task.

/**
 * The automations list's starters. The draft starter's reply asks what the
 * automation should do before the agent runs, so it starts from the user's
 * answer rather than a guess.
 */
export const automationsListSuggestions: ChatSuggestion[] = [
  {
    label: "Help draft an automation",
    prompt: "Help me draft an automation.",
    reply: `I can help you draft an automation. What would you like it to do?
1. Alert someone when a specific moment comes up on a call
2. Send a regular digest of what customers are raising
3. Send each teammate weekly coaching goals

Pick one, or describe what you have in mind, and I'll draft it for you to review.`,
  },
  {
    label: "Review existing automations",
    prompt:
      "Review all the enabled automations: where they overlap, whether each schedule fits its call volume, and whether their recent reports are bringing new insights. Recommend what to change.",
  },
];

const newAutomationSuggestions: ChatSuggestion[] = [
  {
    label: "Draft an automation from recent calls",
    prompt:
      "Look at recent calls on this playbook and propose an automation that would catch the most useful recurring pattern.",
  },
];

const existingAutomationSuggestions: ChatSuggestion[] = [
  {
    label: "What have recent reports found?",
    prompt:
      "Summarize what this automation's last five reports found: what keeps coming up and what changed. If the reports have started repeating themselves, update the instructions to focus on what's important.",
  },
  {
    label: "Improve the instructions",
    prompt:
      "Review this automation's instructions for vague or low-signal guidance, check them against the recent calls it would run over, and propose sharper instructions I can review.",
  },
  {
    label: "Preview reports and recipients",
    prompt:
      "For the most recent period, show which reports this automation would create and who would receive each. Note anyone skipped for having no calls, and flag expected recipients who aren't included.",
  },
  {
    label: "Preview a run",
    prompt:
      "Run a preview of this automation against recent calls and assess the quality of the report. Suggest edits to improve automation quality.",
  },
];

/** A drafting starter for a new automation, tuning starters for a saved one. */
export function getAutomationConfigSuggestions(args: {
  isNewAutomation: boolean;
}): ChatSuggestion[] {
  return args.isNewAutomation
    ? newAutomationSuggestions
    : existingAutomationSuggestions;
}

/** Starters for one run of an automation. */
export const automationRunSuggestions: ChatSuggestion[] = [
  {
    label: "Tune the instructions from this run",
    prompt:
      "Based on the run I'm viewing, suggest changes to the instructions that would make the next run more useful.",
  },
  {
    label: "Spot trends across runs",
    prompt:
      "Compare what the run I'm viewing produced with this automation's earlier runs. What is coming up more often than before, what has stopped coming up, and what is new?",
  },
];

/** Starters for a saved report. */
export const automationArtifactSuggestions: ChatSuggestion[] = [
  {
    label: "Spot trends across reports",
    prompt:
      "Compare the report I'm viewing with this automation's earlier reports. What is coming up more often than before, what has stopped coming up, and what is new?",
  },
];
